/**
 * Global Aura Assistant Component
 * Persistent AI assistant providing contextual help across all features
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { LifebuoyIcon, XMarkIcon } from '@heroicons/react/24/outline';

const PANEL_MOTION = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 6 },
  transition: { duration: 0.4, ease: 'easeOut' },
};

const AuraAssistant = ({ screenName, user, context }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [explanation, setExplanation] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    // Auto-fetch explanation when component mounts
    if (screenName) {
      fetchExplanation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenName]);

  const fetchExplanation = async () => {
    try {
      setLoading(true);
      const response = await axios.post('/api/platform/aura/explain', null, {
        params: {
          screen_name: screenName,
          context: context ? JSON.stringify(context) : null
        }
      });
      setExplanation(response.data.data?.explanation || '');
    } catch (err) {
      console.error('Error fetching Aura explanation:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get('/api/platform/aura/suggestions');
      setSuggestions(response.data.data?.suggestions || []);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    }
  };

  return (
    <>
      {/* Aura Button - Floating */}
      <motion.button
        {...PANEL_MOTION}
        className="fixed bottom-6 right-6 z-40 btn-primary w-14 h-14 rounded-full"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            fetchSuggestions();
          }
        }}
      >
        <LifebuoyIcon className="w-6 h-6" />
      </motion.button>

      {/* Aura Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            {...PANEL_MOTION}
            className="fixed right-6 bottom-24 z-40 w-80 surface-emphasis surface-dark flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-hairline shrink-0">
              <div className="flex items-center gap-2">
                <LifebuoyIcon className="w-5 h-5 text-secondary" />
                <span className="font-display text-h2 text-primary">Aura</span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="icon-btn"
                aria-label="Close Aura"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 max-h-96 overflow-y-auto">
              {/* Explanation */}
              {screenName && (
                <div className="mb-4">
                  <h3 className="text-primary font-medium mb-2 text-body">About this screen</h3>
                  {loading ? (
                    <div className="text-secondary text-sm">Aura is thinking...</div>
                  ) : explanation ? (
                    <p className="text-secondary text-sm leading-relaxed">{explanation}</p>
                  ) : (
                    <p className="text-faint text-sm">No explanation available</p>
                  )}
                </div>
              )}

              {/* Suggestions */}
              {suggestions.length > 0 && (
                <div>
                  <h3 className="text-primary font-medium mb-2 text-body">Suggestions</h3>
                  <div className="space-y-2">
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="p-3 light-surface bg-bone-raised border border-hairline rounded-lg"
                      >
                        <p className="text-secondary text-sm">{suggestion}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No suggestions */}
              {suggestions.length === 0 && !loading && (
                <div className="text-center text-faint text-sm py-4">
                  No suggestions at the moment
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AuraAssistant;
